import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import "dayjs/locale/pt-br";
import {
  Outlet,
  useBlocker,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Paper,
  Slide,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Zoom,
} from "@mui/material";
import api from "../../services/api";
import AlertModal from "../../componentes/alertModal";
import { cidrsInvalidos } from "../../utils/validateCidr";

export default function Configuracoes() {
  const [subTitle, setSubTitle] = useState("");
  const [changes, setChanges] = useState(null);
  const [rerender, setRerender] = useState(true);
  const [snackbar, setSnackbar] = useState(null);
  const [settings, setSettings] = useState([]);
  const [erpErrors, setErpErrors] = useState({
    servidorErp: false,
    portaErp: false,
    bancoErp: false,
    userErp: false,
    senhaErp: false,
  });
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const context = useOutletContext();
  const theme = useTheme();
  const navigate = useNavigate();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  const sm = useMediaQuery(theme.breakpoints.up("sm"));

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return currentLocation.pathname !== nextLocation.pathname && changes;
  });
  const styles = {
    changeAlert: {
      position: "fixed",
      bottom: 20,
      left: 0,
      right: 0,
    },
    changeAlertCard: {
      borderRadius: "5px",
      padding: "5px 15px",
      width: { xs: "95%", md: "650px" },
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
    titleAndLinkPage: {
      flexGrow: 1,
      fontWeight: 400,
      fontSize: "1.25rem",
      mt: 1,
      mb: 2,
    },
    text: {
      fontSize: "0.875rem",
      color: theme.palette.text.primary,
      fontWeight: 400,
    },
  };

  useEffect(() => {
    handleGetSettings();
    permissionValidate();
  }, []);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setOpenAlertModal(true);
    }
  }, [blocker]);

  async function permissionValidate() {
    try {
      const response = await api.get("/authPermissionClient");

      if (response.data.message !== "Permission is valid") {
        navigate("/homeIn");
      }
    } catch (error) {
      console.error(error);
    }
  }

  // requisições a api
  async function handleGetSettings() {
    try {
      const response = await api.get("/settings/findMany");
      if (response.data.status === "Error") {
        setSnackbar({
          children: "Error: Não foi possível carregar as configurações",
          severity: "error",
        });
      } else {
        if (response.data) {
          setSettings(response.data);
        }
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: `Error:${
          error?.message || "Não foi possível se conectar com o servidor"
        } `,
        severity: "error",
      });
    }
  }
  async function handleUpdateSettings() {
    try {
      const logString = localStorage.getItem("logs");
      const logsId = logString ? JSON.parse(logString)?.id : null;
      let settingsOld = settings?.find(
        (item) => item.produto === changes.product,
      );

      if (
        !validateInputs({ ...settingsOld?.configuracoes, ...changes.settings })
      ) {
        setSnackbar({
          children: `Atenção: Preencha todos os campos obrigatórios`,
          severity: "warning",
        });
        return;
      }

      const response = await api.put("settings/update", {
        product: changes.product,
        settings: { ...settingsOld?.configuracoes, ...changes.settings },
        logsLoginClienteId: logsId,
      });

      if (response.data.status === "Error") {
        setSnackbar({
          children: `Error: Não foi possível atualizar as configurações do ${changes.product} `,
          severity: "error",
        });
      } else {
        setSnackbar({
          children: `As configurações do ${changes.product} foram atualizadas com sucesso!`,
          severity: "success",
        });
        //atualiza settings
        setSettings((data) =>
          data.some((item) => item.produto === changes.product)
            ? data.map((item) =>
                item.produto === changes.product ? response.data : item,
              )
            : [...data, response.data],
        );
        setChanges(false);
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        children: `Error:${
          error?.message || "Não foi possível se conectar com o servidor"
        } `,
        severity: "error",
      });
    }
  }
  function clearChanges() {
    setChanges(null);
    setRerender(!rerender);
  }

  const validateInputs = ({
    servidorErp,
    portaErp,
    bancoErp,
    userErp,
    senhaErp,
    erpStatus,
    erpType,
    erpBancos,
    modoErp,
    erpProvedores,
  }) => {
    if (erpStatus) {
      // Validação por card: o `erpType` da raiz não vale, cada provedor tem
      // o seu. Só os ativos são checados.
      if (modoErp === "redeNeutra") {
        if (!Array.isArray(erpProvedores) || erpProvedores.length === 0) {
          return false;
        }
        const ativos = erpProvedores.filter(
          (provedor) => provedor?.ativo !== false,
        );
        // Tudo desativado é estado válido: não há card ativo para validar.
        if (ativos.length === 0) return true;

        // Faixa inválida não pode passar: o backend descarta o que não
        // parseia, e um card cujo escopo inteiro foi descartado deixa de ser
        // consultado — a tela diria "escopado" e a busca nunca o alcançaria.
        const escopoInvalido = ativos.some(
          (provedor) =>
            cidrsInvalidos((provedor.faixasPrivadas || []).join(",")).length >
              0 ||
            cidrsInvalidos((provedor.blocosPublicos || []).join(",")).length >
              0,
        );
        if (escopoInvalido) return false;

        return !ativos.some(
          (provedor) =>
            // Sem nome, o resultado não diz de quem é o cliente.
            !(provedor.nome || "").trim() ||
            !(provedor.host || "").trim() ||
            !String(provedor.port || "").trim() ||
            !(provedor.database || "").trim() ||
            !(provedor.user || "").trim() ||
            !(provedor.password || "").trim() ||
            (provedor.sshEnabled &&
              (!(provedor.sshHost || "").trim() ||
                !(provedor.sshUser || "").trim())),
        );
      }

      if (erpType === "mk" || erpType === "voalle") {
        if (!erpBancos || !Array.isArray(erpBancos)) return false;
        const hasError = erpBancos.some(
          (b) =>
            !(b.host || "").trim() ||
            !(b.port || "").trim() ||
            !(b.database || "").trim() ||
            !(b.user || "").trim() ||
            !(b.password || "").trim(),
        );
        return !hasError;
      }

      const errors = {
        servidorErp: !(servidorErp || "").trim(),
        portaErp: !(portaErp || "").trim(),
        bancoErp: !(bancoErp || "").trim(),
        userErp: !(userErp || "").trim(),
        senhaErp: !(senhaErp || "").trim(),
      };
      setErpErrors(errors);
      const hasError = Object.values(errors).some(Boolean);
      if (hasError) {
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
    // Aqui você coloca a lógica real de salvar, p.ex:
    // api.post('/config', { produto, ... });
  };

  return (
    <>
      <Breadcrumbs
        separator={<Typography sx={styles.titleAndLinkPage}>/</Typography>}
      >
        <Typography sx={styles.titleAndLinkPage}>Configurações</Typography>
        <Typography
          sx={{ ...styles.titleAndLinkPage, color: theme.palette.text.primary }}
        >
          {subTitle}
        </Typography>
      </Breadcrumbs>

      <Outlet
        key={"ouT" + rerender}
        context={{
          setChanges,
          setSubTitle,
          ...context,
          settings,
          erpErrors,
          setErpErrors,
          changes,
        }}
      />

      <Stack alignItems={"center"} sx={styles.changeAlert}>
        {" "}
        <Zoom in={!openAlertModal && changes}>
          <Paper sx={styles.changeAlertCard}>
            <Typography sx={styles.text}>
              {`${
                md
                  ? "Atenção, você tem alterações não salvas!"
                  : "Alterações não salvas!"
              }`}
            </Typography>
            <Stack gap={1} direction={"row"} alignItems={"center"}>
              <Button sx={styles.text} onClick={() => clearChanges()}>
                Redefinir
              </Button>
              <Button
                sx={{
                  ...styles.text,
                  color: "white",
                  height: "30px",
                }}
                color="success"
                variant="contained"
                onClick={() => handleUpdateSettings()}
              >
                {`Salvar ${md ? "alterações" : ""}`}
              </Button>
            </Stack>
          </Paper>
        </Zoom>
      </Stack>

      <AlertModal
        openModal={openAlertModal}
        setOpenModal={setOpenAlertModal}
        buttonText={"Salvar"}
        buttonCancelText={"Voltar"}
        confirmar={false}
        handleButton={() => {
          handleUpdateSettings();
          setOpenAlertModal(false);
          setChanges(null);
          blocker.proceed();
        }}
        handleCancel={() => {
          setOpenAlertModal(false);
          blocker.reset();
        }}
        severity={"warning"}
        singleButton={false}
        textContent={`Você tem alterações não salvas em ${changes?.product} , deseja salvar?`}
      />

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </>
  );
}
